import type { TaskNote } from '../taskEditModal.types';

interface NotesListProps {
  notes: TaskNote[];
  editingNoteId: string | null;
  editingNoteText: string;
  profileId?: string;
  setEditingNoteText: (value: string) => void;
  onEdit: (note: TaskNote) => void;
  onDelete: (id: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
}

/**
 * Editable notes list for the Task Edit Modal (edit-mode form).
 * Inline edit textarea + view mode with author/date header and hover edit/delete
 * actions, gated on `noteObj.authorId === profileId`.
 */
export function NotesList({
  notes,
  editingNoteId,
  editingNoteText,
  profileId,
  setEditingNoteText,
  onEdit,
  onDelete,
  onSaveEdit,
  onCancelEdit,
}: NotesListProps) {
  return (
    <div className="px-4 pb-4">
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {notes.map((noteObj) => (
          <div key={noteObj.id} className="p-2 bg-gray-50 rounded-lg group">
            {editingNoteId === noteObj.id ? (
              // Edit mode - inline editing
              <div>
                <div className="relative mb-1 px-2">
                  <span className="text-xs text-gray-600 font-medium">
                    {noteObj.authorName || 'Unknown'} — {new Date(noteObj.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {new Date(noteObj.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {noteObj.updatedAt && ' (edited)'}
                  </span>
                </div>
                <textarea
                  value={editingNoteText}
                  onChange={(e) => setEditingNoteText(e.target.value)}
                  onBlur={() => onSaveEdit()}
                  className="w-full px-2 py-1.5 text-sm bg-white border-2 border-blue-500 rounded resize-none"
                  style={{
                    lineHeight: '1.5',
                    boxSizing: 'border-box',
                    outline: 'none',
                    margin: 0,
                    fontFamily: 'inherit',
                    display: 'block',
                    overflow: 'hidden'
                  }}
                  rows={Math.max(1, editingNoteText.split('\n').length)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSaveEdit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      onCancelEdit();
                    }
                  }}
                />
              </div>
            ) : (
              // View mode
              <div>
                <div className="relative mb-1 px-2">
                  <span className="text-xs text-gray-600 font-medium">
                    {noteObj.authorName || 'Unknown'} — {new Date(noteObj.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {new Date(noteObj.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {noteObj.updatedAt && ' (edited)'}
                  </span>
                  {/* Only show edit/delete for user's own notes */}
                  {noteObj.authorId === profileId && (
                    <div className="absolute top-0 right-2 flex gap-1 items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onEdit(noteObj);
                        }}
                        className="text-gray-400 hover:text-blue-500 p-1 flex items-center justify-center"
                        title="Edit note"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDelete(noteObj.id);
                        }}
                        className="text-gray-400 hover:text-red-500 text-lg p-1 flex items-center justify-center leading-none"
                        title="Delete note"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-900 px-2 py-1.5 border-2 border-transparent rounded" style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap', boxSizing: 'border-box', margin: 0 }}>{noteObj.content}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
